import { ALL_VENDORS, createBomMapping, getBomMaterialPicks, type BomMaterialPick, type BomRow } from '@api/bom-management';

import type { SelectOption } from './makerOptions';

/** One BOM line as a form holds it. `quantity` blank means not yet typed. */
export interface BomLineDraft {
    materialId: string | null;
    quantity: number | '';
}

/**
 * The materials a recipe may use: active ones, never a STAGE.
 *
 * The picks endpoint already drops retired materials. STAGE is dropped here because a stocked
 * intermediate is consumed as an alternate for the raw line it embodies, never as a base line,
 * and the backend refuses it on write; the picker should not offer what the save refuses.
 */
export const usableBomMaterials = (picks: BomMaterialPick[]): BomMaterialPick[] =>
    picks.filter((m) => (m.materialType as string) !== 'STAGE');

export const loadBomMaterialPicks = async (): Promise<BomMaterialPick[]> => {
    const picks = await getBomMaterialPicks().catch(() => [] as BomMaterialPick[]);
    return usableBomMaterials(picks);
};

export const materialOptions = (picks: BomMaterialPick[]): SelectOption[] =>
    picks.map((m) => ({
        value: m.materialId,
        label: m.name && m.name !== m.materialId ? `${m.name} — ${m.materialId}` : m.materialId,
    }));

/**
 * Start a vendor's BOM as a copy of the all-vendors lines.
 *
 * A vendor BOM that differs in one line still has to state all of them: once a vendor has
 * lines of its own, those are the only lines its production reads. Retyping twelve lines to
 * change one is how a brand ends up with a BOM that is wrong in the eleven it retyped, so the
 * copy is the starting point and the edit happens afterwards. Returns how many lines were
 * written; zero when the product has no all-vendors lines to copy.
 */
export const copySharedBomLines = async (
    rows: BomRow[],
    productId: string,
    vendorId: string,
): Promise<number> => {
    const shared = rows.filter((r) => r.productId === productId && !r.vendorId);
    for (const line of shared) {
        await createBomMapping({
            productId: line.productId,
            materialId: line.materialId,
            quantity: line.quantity,
            vendorId,
        });
    }
    return shared.length;
};

export interface BomVendorPill {
    /** '' = every line, ALL_VENDORS = the shared recipe, else a vendor id. */
    value: string;
    label: string;
    count: number;
}

/**
 * The recipes a set of BOM lines holds, as pills: every line, the shared ("all vendors")
 * recipe, then one per vendor that actually has lines of its own. "Who has a BOM of their
 * own here", not "who could have one".
 */
export const bomVendorPills = (rows: BomRow[]): BomVendorPill[] => {
    const byVendor = new Map<string, { label: string; count: number }>();
    let shared = 0;
    for (const row of rows) {
        if (!row.vendorId) {
            shared++;
            continue;
        }
        const held = byVendor.get(row.vendorId);
        byVendor.set(row.vendorId, {
            label: held?.label || row.vendorName || row.vendorId,
            count: (held?.count ?? 0) + 1,
        });
    }
    return [
        { value: '', label: 'All lines', count: rows.length },
        ...(shared > 0 ? [{ value: ALL_VENDORS, label: 'All vendors', count: shared }] : []),
        ...[...byVendor]
            .sort((a, b) => a[1].label.localeCompare(b[1].label))
            .map(([vendorId, { label, count }]) => ({ value: vendorId, label, count })),
    ];
};
